import {TextArea} from '@wix/wix-ui-tpa/cssVars';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import {debounce} from 'lodash';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {useIsRenderedFrom} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import {st, classes as stylableClasses} from './ProductCustomText.st.css';

const DEBOUNCE_DELAY_MS = 150;

export enum ProductCustomTextDataHooks {
  container = 'product-custom-text-container',
  textArea = 'product-custom-text-text-area',
}
export interface ProductCustomTextProps extends IProvidedTranslationProps, ProvidedGlobalProps {
  handleOnChange?(textAreaElemValue: string): void;
  isRequired: boolean;
  maxLength: number;
  title: string;
  errorIndex: number;
  className?: string;
  showErrors?: boolean;
}

/* eslint-disable sonarjs/cognitive-complexity */
const ProductCustomTextComponent: FC<ProductCustomTextProps & IProvidedTranslationProps> = ({
  handleOnChange,
  isRequired,
  maxLength,
  title,
  errorIndex,
  className,
  showErrors = true,
  t,
  globals: {
    isProductSubmitted,
    userInputErrors,
    scrollAndFocusInputTarget,
    isQuickView,
    experiments,
    handleUserInput,
    validateUserInputsChanges,
    useExperiments,
    userInputs,
  },
}) => {
  const isRenderedFromStickyDrawer = useIsRenderedFrom('stickyDrawer');
  const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StickyAddToCartMobile);

  const globalTextValue = isStickyAddToCartEnabled ? userInputs?.text?.[errorIndex] ?? '' : '';
  const [value, setValue] = useState(globalTextValue);
  const [isFocused, setIsFocused] = useState(false);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (isStickyAddToCartEnabled && !isEditingRef.current) {
      setValue(globalTextValue);
    }
  }, [globalTextValue, isStickyAddToCartEnabled]);

  const debouncedUpdateStore = useMemo(
    () =>
      debounce((textValue: string) => {
        handleUserInput(UserInputType.Text, textValue, errorIndex);
        validateUserInputsChanges();
      }, DEBOUNCE_DELAY_MS),
    [handleUserInput, validateUserInputsChanges, errorIndex]
  );

  useEffect(() => {
    return () => debouncedUpdateStore.cancel();
  }, [debouncedUpdateStore]);

  function onChange(newValue: string) {
    setValue(newValue);
    if (isStickyAddToCartEnabled) {
      debouncedUpdateStore(newValue);
    }
  }

  function onBlur(event: React.FocusEvent<HTMLTextAreaElement>) {
    if (!isStickyAddToCartEnabled) {
      handleOnChange(event.target.value);
      return;
    }

    isEditingRef.current = false;
    debouncedUpdateStore.flush();
  }

  const textAreaContainerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const isScrollAndFocusInputTarget =
      UserInputType.Text === scrollAndFocusInputTarget?.inputType && errorIndex === scrollAndFocusInputTarget?.index;

    if (isScrollAndFocusInputTarget && textAreaContainerRef.current) {
      scrollAndFocusUserInput(UserInputType.Text, textAreaContainerRef.current, isQuickView);
    }
  }, [scrollAndFocusInputTarget, errorIndex, textAreaContainerRef, isQuickView, experiments]);

  const textAreaClasses = st(stylableClasses.customTextRoot, {
    midHeight: isFocused && maxLength > 25 && maxLength <= 250,
    fullHeight: isFocused && maxLength > 250,
    stickyDrawer: isStickyAddToCartEnabled && isRenderedFromStickyDrawer,
  });

  const label = isRequired ? title : `${title} (${t('OPTIONAL')})`;

  const errorProps =
    showErrors || !isStickyAddToCartEnabled
      ? {
          newErrorMessage: true,
          error: isProductSubmitted && userInputErrors[UserInputType.Text][errorIndex],
          errorMessage: t('REQUIRED_FIELD'),
        }
      : {};

  return (
    <div ref={textAreaContainerRef} data-hook={ProductCustomTextDataHooks.container}>
      <TextArea
        data-hook={ProductCustomTextDataHooks.textArea}
        className={classNames(textAreaClasses, className)}
        label={label}
        ariaLabel={label}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => {
          setIsFocused(true);
          if (isStickyAddToCartEnabled) {
            isEditingRef.current = true;
          }
        }}
        onBlur={(event) => onBlur(event)}
        value={value}
        maxLength={maxLength}
        required={isRequired}
        showAsterisk={isRequired}
        showCharCount
        {...errorProps}
      />
    </div>
  );
};

export const ProductCustomText: FC<ProductCustomTextProps> = withGlobalProps(
  withTranslations('globals.texts')(ProductCustomTextComponent)
);
